"use client";

// Modal pra solicitar o PDF do produto por e-mail. Aberto pelo CTA "Baixar
// PDF" na página de detalhe. POST passa por /api/products/{id}/request-pdf
// (proxy server-side com X-Site-Api-Key). Backend retorna 202 (lead criado,
// envio do PDF assíncrono por e-mail).
//
// Mantém compat: sessionStorage marca já-enviou pra não pedir os dados de
// novo na mesma sessão.

import { useEffect, useRef, useState } from "react";
import { useLangContext } from "../lib/lang-context";

const SUBMIT_KEY_PREFIX = "mbm:pdf-sent:";

function alreadySentThisSession(productId) {
  if (typeof window === "undefined") return false;
  try {
    return !!window.sessionStorage.getItem(`${SUBMIT_KEY_PREFIX}${productId}`);
  } catch {
    return false;
  }
}

function markSentThisSession(productId) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      `${SUBMIT_KEY_PREFIX}${productId}`,
      new Date().toISOString()
    );
  } catch {
    // sessionStorage bloqueado — UX degrada (usuário pode reenviar, mas o
    // backend já tem rate-limit 10/IP/10min).
  }
}

export default function ProductPdfRequest({
  productId,
  productName,
  open,
  onClose,
}) {
  const { t } = useLangContext();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(() => alreadySentThisSession(productId));
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);

  // Fecha no ESC.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Trava o scroll da página com o modal aberto. A trava vai no
  // documentElement, não no body: `html` é `overflow-x: clip`, então o
  // overflow do body não propaga mais pro viewport e seria no-op.
  useEffect(() => {
    if (!open) return undefined;
    const root = document.documentElement;
    const prev = root.style.overflowY;
    root.style.overflowY = "hidden";
    return () => {
      root.style.overflowY = prev;
    };
  }, [open]);

  // Foco inicial no primeiro campo.
  useEffect(() => {
    if (open && !done) {
      window.requestAnimationFrame(() => firstInputRef.current?.focus());
    }
  }, [open, done]);

  if (!open) return null;

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const body = {
      name: String(form.get("name") ?? "").trim(),
      email: String(form.get("email") ?? "").trim(),
      phone: String(form.get("phone") ?? "").trim(),
      empresaSolicitante:
        String(form.get("empresa") ?? "").trim() || undefined,
      consent: form.get("consent") === "on",
    };

    try {
      const res = await fetch(`/api/products/${productId}/request-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.ok === false) {
        throw new Error(
          data?.error || (t("pdf_error_generic") || "Não foi possível enviar agora.")
        );
      }
      markSentThisSession(productId);
      setDone(true);
      formEl.reset();
    } catch (err) {
      setError(err?.message || (t("pdf_error_generic") || "Erro."));
    } finally {
      setSubmitting(false);
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="pdf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-modal-title"
      >
        <button
          type="button"
          className="pdf-modal-close"
          aria-label={t("pdf_close") || "Fechar"}
          onClick={() => onClose?.()}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16"
               fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" focusable="false">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        {done ? (
          <div className="pdf-success">
            <h3 id="pdf-modal-title">
              {t("pdf_success_title") || "Pedido recebido!"}
            </h3>
            <p>
              {t("pdf_success_desc") ||
                "Você receberá o PDF em alguns minutos no e-mail informado. Confira também a caixa de spam."}
            </p>
            <button
              type="button"
              className="cta-btn"
              onClick={() => onClose?.()}
            >
              {t("pdf_close") || "Fechar"}
            </button>
          </div>
        ) : (
          <form className="pdf-form" onSubmit={handleSubmit} noValidate>
            <h3 id="pdf-modal-title" className="pdf-modal-title">
              {t("pdf_modal_title") || "Baixar PDF do produto"}
            </h3>
            <p className="pdf-form-intro">
              {productName
                ? `${t("pdf_form_intro_prefix") || "Receba o PDF de"} ${productName} ${
                    t("pdf_form_intro_suffix") || "no seu e-mail."
                  }`
                : t("pdf_form_intro") ||
                  "Preencha seus dados e enviaremos o PDF deste produto no seu e-mail."}
            </p>

            <div className="pdf-form-grid">
              <label className="field">
                <span>
                  {t("pdf_label_name") || "Nome"} <em aria-hidden="true">*</em>
                </span>
                <input
                  ref={firstInputRef}
                  type="text"
                  name="name"
                  required
                  maxLength={200}
                  autoComplete="name"
                />
              </label>
              <label className="field">
                <span>
                  {t("pdf_label_email") || "E-mail"} <em aria-hidden="true">*</em>
                </span>
                <input
                  type="email"
                  name="email"
                  required
                  maxLength={255}
                  inputMode="email"
                  autoComplete="email"
                />
              </label>
              <label className="field">
                <span>
                  {t("pdf_label_phone") || "Telefone"} <em aria-hidden="true">*</em>
                </span>
                <input
                  type="tel"
                  name="phone"
                  required
                  maxLength={30}
                  inputMode="tel"
                  autoComplete="tel"
                />
              </label>
              <label className="field">
                <span>{t("pdf_label_company") || "Empresa (opcional)"}</span>
                <input
                  type="text"
                  name="empresa"
                  maxLength={200}
                  autoComplete="organization"
                />
              </label>
            </div>

            <label className="pdf-form-consent">
              <input type="checkbox" name="consent" required />
              <span>
                {t("pdf_label_consent") ||
                  "Aceito receber o PDF e comunicações relacionadas (LGPD)."}
              </span>
            </label>

            {error && (
              <p role="alert" className="pdf-form-error">
                {error}
              </p>
            )}

            <div className="pdf-form-actions">
              <button
                type="button"
                className="cta-btn ghost"
                onClick={() => onClose?.()}
                disabled={submitting}
              >
                {t("pdf_cancel") || "Cancelar"}
              </button>
              <button
                type="submit"
                className="cta-btn"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting
                  ? t("pdf_submitting") || "Enviando…"
                  : t("pdf_submit") || "Enviar e receber PDF"}
                <span className="arr" aria-hidden="true">→</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
